import { ChangeDetectorRef, Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { Filter, FilterOption, StaticFilter } from '../../models/dynamic-filter.model';
import { SectionDefinitionEntityType } from '../../models/section-definition.model';
import { SubstationAppliedFilter } from '../../models/substation.model';
import { DateType } from '../../enums/date-type';
import { UserType } from '../../enums/user-type';
import { RegionService } from '../../services/region.service';
import { AreaService } from '../../services/area.service';
import { ToastrService } from 'ngx-toastr';
import { UserService } from '../../services/user.service';
import { TransmissionStationService } from '../../services/transmission-station.service';
import { EntityModelCustomizationService } from '../../services/entity-model-customization.service';
import { EmcOptions } from '../../models/entity-model-customization.model';
import { Region } from '../../models/region.model';
import { Area } from '../../models/area.model';
import { TransmissionStation } from '../../models/transmission-station.model';
import { Purpose } from '../../enums/purpose';
import { SystemComponents } from '../../models/role.model';
import { EMCType } from '../../enums/emc-type';

@Component({
  selector: 'lib-dynamic-filter-window',
  standalone: false,
  templateUrl: './dynamic-filter-window.component.html',
  styleUrls: ['./dynamic-filter-window.component.scss']
})
export class DynamicFilterWindowComponent {
  @Input() entityType!: EMCType;
  @Input() filter: Filter = new Filter();
  @Input() substationTemplateId = 0;
  @Input() cimClass?: number;

  @Output() close = new EventEmitter<void>();
  @Output() addFilter = new EventEmitter<Filter>();

  constructor(
    private regionService: RegionService,
    private areaService: AreaService,
    private toastr: ToastrService,
    private userService: UserService,
    private tsService: TransmissionStationService,
    private cdr: ChangeDetectorRef,
    private emcService: EntityModelCustomizationService
  ) { }

  dynamicFilterOptions: EmcOptions[] = [];

  dateTypes = [
    'Last Update Time',
    'Tagged Date',
    'Validated Date',
    'Audited Date',
    'Review Date',
    'GIS Review Date',
  ];
  selectedDateTypeString = this.dateTypes[0];
  selectedDateType: DateType = DateType.LastUpdateTime;
  selectedMonth = '';
  selectedYear = new Date().getFullYear();
  selectedDateFrom: string | null = null;
  selectedDateTo: string | null = null;
  regions: Region[] = [];
  areas: Area[] = [];
  allAreas: Area[] = [];
  regionSearchText: string = '';
  districtSearchText: string = '';
  tsSearchText: string = '';
  openedFilters: string[] = [];
  selectedRegion: Region | null = null;
  selectedUserType: string | null = null;
  selectedValidationStatus: string | null = null;
  selectedAreas: { id: number; name: string }[] = [];
  allTransmissionStations: TransmissionStation[] = [];
  transmissionStations: TransmissionStation[] = [];
  selectedTransmissionStations: { id: number; name: string }[] = [];
  appliedFilters: SubstationAppliedFilter[] = [];

  searchUsersText: string = '';
  allUsers: { id: number; name: string }[] = [];

  selectedUsers: { id: number; name: string }[] = [];

  userTypes = [
    { label: 'First_Captured', value: 'first' },
    { label: 'Last_Captured', value: 'last' },
    { label: 'Reassigned', value: 'reassigned' },
    { label: 'Validated', value: 'validated' },
    { label: 'Audited', value: 'audited' },
    { label: 'Reviewed', value: 'reviewed' },
  ];

  validationStatuses = [
    { label: 'Complete', value: 1 },
    { label: 'Rejected', value: 4 },
    { label: 'Pending', value: 0 },
    { label: 'Assigned', value: 3 },
  ];

  reviewStatuses = [
    { label: 'Reviewed', value: 1 },
    { label: 'Not Reviewed', value: 0 },
  ];

  auditStatuses = [
    { label: 'Audited', value: 1 },
    { label: 'Not Audited', value: 0 },
  ];

  gisReviewStatuses = [
    { label: 'Not Validated', value: 1 },
    { label: 'Validated', value: 2 },
    { label: 'Auto Validated', value: 3 },
  ];

  gisUpdateStatuses = [
    { label: 'Approved', value: 1 },
    { label: 'Rejected', value: 2 },
  ];

  mapValidationStatus: Record<number, string> = {
    [0]: 'Pending',
    [1]: 'Complete',
    [3]: 'Assigned',
    [4]: 'Rejected',
  };

  mapDateType: Record<number, string> = {
    [2]: 'Last Update Time',
    [3]: 'Tagged Date',
    [6]: 'Validated Date',
    [10]: 'Review Date',
    [11]: 'Audited Date',
  };

  mapUserType: Record<number, string> = {
    [1]: 'First Captured User',
    [2]: 'Last Captured User',
    [3]: 'Reassigned By',
    [4]: 'Validated By',
    [5]: 'Audited By',
    [6]: 'Reviewed By',
  };

  months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  ngOnInit(): void {
    this.getDynamicFilter();
    if (this.filter.filterParams.userType != null && (this.filter.filterParams.userType as number) !== 0) {
      this.getUsersForFilter(this.filter.filterParams);
    }
  }

  getDynamicFilter() {
    this.emcService.getSelectedEmcOptionsForFilter(this.entityType, Purpose.Filter, this.substationTemplateId, this.cimClass).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.dynamicFilterOptions = responseData.data || [];
          if (this.dynamicFilterOptions.some(x => x.staticPropertyName == 'RegionDistrict')) {
            this.getRegions();
          }
        } else {
          this.toastr.error(responseData?.message || 'Failed to fetch selected EMC options');
        }
      },
      error: (error) => {
        this.toastr.error('Error occurred while fetching selected EMC options');
      },
    });
  }

  onDateTypeChange() {
    switch (this.filter.filterParams.dateTypeString) {
      case 'Last Update Time':
        this.filter.filterParams.dateType = DateType.LastUpdateTime;
        break;
      case 'Tagged Date':
        this.filter.filterParams.dateType = DateType.TaggedDate;
        break;
      case 'Validated Date':
        this.filter.filterParams.dateType = DateType.ValidatedDate;
        break;
      case 'Review Date':
        this.filter.filterParams.dateType = DateType.ReviewDate;
        break;
      case 'Audited Date':
        this.filter.filterParams.dateType = DateType.AuditedDate;
        break;
      case 'GIS Review Date':
        this.filter.filterParams.dateType = DateType.GisReviewDate;
        break;
      default:
        this.filter.filterParams.dateType = DateType.LastUpdateTime;
        break;
    }
  }

  returnDateTypeFromString(dateTypeString: string) {
    switch (dateTypeString) {
      case 'Last Update Time':
        return DateType.LastUpdateTime;
      case 'Tagged Date':
        return DateType.TaggedDate;
      case 'Validated Date':
        return DateType.ValidatedDate;
      case 'Review Date':
        return DateType.ReviewDate;
      case 'Audited Date':
        return DateType.AuditedDate;
      case 'GIS Review Date':
        return DateType.GisReviewDate;
      default:
        return DateType.LastUpdateTime;
    }
  }

  toggleFilter(filter: string) {
    const index = this.openedFilters.indexOf(filter);
    if (index > -1) {
      this.openedFilters.splice(index, 1);
    } else {
      this.openedFilters.push(filter);
    }
  }

  onCancel() {
    this.close.emit();
  }

  onWindowClick(event: MouseEvent) {
    event.stopPropagation();
  }

  selectUserType(obj: string | null) {
    if (obj == null || obj == '') this.filter.filterParams.userType = UserType.None;
    else if (obj == 'first') this.filter.filterParams.userType = UserType.FirstCapturedUser;
    else if (obj == 'last') this.filter.filterParams.userType = UserType.LastCapturedUser;
    else if (obj == 'reassigned') this.filter.filterParams.userType = UserType.ReassignedBy;
    else if (obj == 'validated') this.filter.filterParams.userType = UserType.ValidatedBy;
    else if (obj == 'audited') this.filter.filterParams.userType = UserType.AuditedBy;
    else if (obj == 'reviewed') this.filter.filterParams.userType = UserType.ReviewedBy;
  }

  onSelectUserType(option: { label: string; value: string }) {
    this.selectUserType(option.value);
    this.getUsersForFilter(this.filter.filterParams);
  }

  onSelectUser(user: { id: number; name: string }, event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      this.filter.filterParams.selectedUserModels.push(user);
    } else {
      this.filter.filterParams.selectedUserModels = this.filter.filterParams.selectedUserModels.filter((u) => u.id !== user.id);
    }
  }

  isUserSelected(user: { id: number; name: string }): boolean {
    return this.filter.filterParams.selectedUserModels.some((u) => u.id === user.id);
  }

  get filteredUsers() {
    if (!this.filter.filterParams.searchUsersText?.trim()) {
      return this.allUsers;
    }

    return this.allUsers.filter((user) =>
      user.name.toLowerCase().includes(this.filter.filterParams.searchUsersText.toLowerCase())
    );
  }

  getUsersForFilter(staticFilter: StaticFilter) {
    if (
      staticFilter.userType == UserType.FirstCapturedUser ||
      staticFilter.userType == UserType.LastCapturedUser
    ) {
      this.userService
        .getUsersForFilter(SystemComponents.Mobile)
        .subscribe((res: any) => {
          this.allUsers = res.data;
        });
    } else {
      this.userService
        .getUsersForFilter(SystemComponents.Admin)
        .subscribe((res: any) => {
          this.allUsers = res.data;
        });
    }
  }

  onAddFilter() {
    if (this.filter.filterParams.dateTo != null) {
      const d = new Date(this.filter.filterParams.dateTo);
      d.setHours(23, 59, 59, 999);
      this.filter.filterParams.dateTo = d.toISOString();
    }
    this.addFilter.emit(this.filter);

    this.onCancel();
  }

  selectRegion(region: Region) {
    this.filter.filterParams.selectedRegion = this.filter.filterParams.selectedRegion === region ? null : region;
    this.updateAreas();
  }

  updateAreas() {
    this.areas = this.allAreas.filter(
      (area) =>
        (!this.filter.filterParams.selectedRegion || area.regionId === this.filter.filterParams.selectedRegion.id) &&
        (!this.filter.filterParams.areaSearchText ||
          area.name
            .toLowerCase()
            .includes(this.filter.filterParams.areaSearchText.toLowerCase()))
    );
  }

  updateTransmissionStations() {
    this.transmissionStations = this.allTransmissionStations.filter(
      (ts) =>
        !this.filter.filterParams.tsSearchText ||
        ts.name.toLowerCase().includes(this.filter.filterParams.tsSearchText.toLowerCase())
    );
  }

  get filteredRegions() {
    return this.regions.filter(
      (region) =>
        !this.filter.filterParams.regionSearchText ||
        region.name.toLowerCase().includes(this.filter.filterParams.regionSearchText.toLowerCase())
    );
  }

  isAreaSelected(area: { id: number; name: string }): boolean {
    return this.filter.filterParams.selectedAreaModels.some((a) => a.id == area.id);
  }
  isTsSelected(ts: { id: number; name: string }): boolean {
    return this.filter.filterParams.selectedTransmissionStations.some((tsId) => tsId == ts.id);
  }

  onAreaSelect(area: { id: number; name: string }, event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      this.filter.filterParams.selectedAreaModels.push(area);
    } else {
      this.filter.filterParams.selectedAreaModels = this.filter.filterParams.selectedAreaModels.filter((a) => a.id !== area.id);
    }
  }

  onTsSelect(ts: { id: number; name: string }, event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      this.filter.filterParams.selectedTsModels.push(ts);
    } else {
      this.filter.filterParams.selectedTsModels =
        this.filter.filterParams.selectedTsModels.filter((ts) => ts.id !== ts.id);
    }
  }

  private getRegions() {
    this.regionService.getAllForSelect().subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.regions = responseData.data.map((region: Region) => ({
            ...region,
            isExpanded: false,
          }));
          this.getAreas();
        } else if (response?.status === 204) {
          this.regions = [];
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  private getAreas() {
    this.areaService.getAllForSelect().subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.allAreas = responseData.data.map((area: Area) => ({
            ...area,
            checked: false,
          }));
          this.updateAreas();
          this.getAllTss();
        } else if (response?.status === 204) {
          this.allAreas = [];
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  private getAllTss() {
    this.tsService.getAllForMappings().subscribe({
      next: (response) => {
        this.allTransmissionStations = response.data;
        this.updateTransmissionStations();
      },
      error: (_) => this.toastr.error('An error occurred.'),
    });
  }

  getDynamicFilterValue(attributeId: number, field: 'from' | 'to'): Date | null {
    const filter = this.filter.dynamicFilters.find(f => f.attributeDefinitionId === attributeId);
    return filter ? filter[field] || null : null;
  }

  onDynamicDateChange(attributeId: number, label: string, field: 'from' | 'to', event: Event): void {
    const input = event.target as HTMLInputElement;
    const dateValue = input.value ? new Date(input.value) : null;

    let filter = this.filter.dynamicFilters.find(f => f.attributeDefinitionId === attributeId);
    if (!filter) {
      filter = {
        attributeDefinitionId: attributeId,
        attributeLabel: label,
        selectedOptions: [],
        optionDefinitions: [],
        from: null,
        to: null
      };
      this.filter.dynamicFilters.push(filter);
    }

    filter[field] = dateValue;

    const hasDates = filter.from || filter.to;
    if (!hasDates) {
      this.filter.dynamicFilters = this.filter.dynamicFilters.filter(f => f.attributeDefinitionId !== attributeId);
    }
  }

  isValidationStatusSelected(status: number): boolean {
    return this.filter.filterParams.statuses.includes(status);
  }

  onValidationStatusChange(status: number, event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      this.filter.filterParams.statuses.push(status);
    } else {
      this.filter.filterParams.statuses = this.filter.filterParams.statuses.filter((s) => s !== status);
    }
  }

  isOptionSelected(attrId: number, optionId: number): boolean {
    const filter = this.filter.dynamicFilters.find(f => f.attributeDefinitionId === attrId);
    return filter ? (filter.selectedOptions?.includes(optionId) ?? false) : false;
  }

  onOptionChange(
    attrLabel: string,
    attrId: number,
    optionId: number,
    optionValue: string,
    event: Event
  ) {
    const input = event.target as HTMLInputElement;
    const checked = input.checked;

    let filter = this.filter.dynamicFilters.find(f => f.attributeDefinitionId === attrId);

    if (!filter) {
      filter = {
        attributeDefinitionId: attrId,
        attributeLabel: attrLabel,
        optionDefinitions: [],
        selectedOptions: [],
      };
      this.filter.dynamicFilters.push(filter);
    }

    filter.optionDefinitions = filter.optionDefinitions || [];
    filter.selectedOptions = filter.selectedOptions || [];

    if (checked) {
      if (!filter.optionDefinitions.some(o => o.optionId === optionId)) {
        filter.optionDefinitions.push(new FilterOption(attrLabel, optionId, optionValue));
      }
      if (!filter.selectedOptions.includes(optionId)) {
        filter.selectedOptions.push(optionId);
      }
    } else {
      filter.optionDefinitions = filter.optionDefinitions.filter(o => o.optionId !== optionId);
      filter.selectedOptions = filter.selectedOptions.filter(id => id !== optionId);
    }

    if (filter.selectedOptions.length === 0 && !filter.from && !filter.to) {
      this.filter.dynamicFilters = this.filter.dynamicFilters.filter(f => f.attributeDefinitionId !== attrId);
    }
  }
}
